/**
 * Trunnion AI - Tool Selector
 * Multi-select tool whitelist for agent configuration.
 */

import { useState } from 'react';
import { Search, X, Wrench } from 'lucide-react';
import { useToolList } from '@/hooks/queries/useTools';
import type { ToolWhitelistEntry } from '@/types';

interface ToolSelectorProps {
  selectedTools: ToolWhitelistEntry[];
  onChange: (tools: ToolWhitelistEntry[]) => void;
}

export function ToolSelector({ selectedTools, onChange }: ToolSelectorProps) {
  const [search, setSearch] = useState('');
  const { data: toolsData } = useToolList({ search, isActive: true });

  const tools = toolsData?.tools ?? [];
  const selectedIds = new Set(selectedTools.map((t) => t.toolId));

  const addTool = (toolId: string, toolName: string) => {
    if (selectedIds.has(toolId)) return;
    onChange([
      ...selectedTools,
      {
        toolId,
        toolName,
        parameterBounds: [],
        requiresApproval: false,
        maxCallsPerExecution: 10,
        allowedClassifications: ['UNCLASSIFIED'],
      },
    ]);
  };

  const removeTool = (toolId: string) => {
    onChange(selectedTools.filter((t) => t.toolId !== toolId));
  };

  return (
    <div className="flex flex-col gap-3">
      <label className="text-sm font-medium text-trunnion-slate-200">
        Tool Whitelist
      </label>

      {/* Selected tools */}
      {selectedTools.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selectedTools.map((tool) => (
            <span
              key={tool.toolId}
              className="inline-flex items-center gap-1 rounded-md border border-trunnion-slate-600 bg-trunnion-slate-800 px-2 py-1 text-xs text-trunnion-slate-200"
            >
              <Wrench className="h-3 w-3 text-trunnion-gold-500" />
              {tool.toolName}
              <button
                onClick={() => removeTool(tool.toolId)}
                className="ml-0.5 rounded hover:bg-trunnion-slate-700 p-0.5"
              >
                <X className="h-3 w-3 text-trunnion-slate-400" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-trunnion-slate-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search tools..."
          className="w-full rounded-lg border border-trunnion-slate-600 bg-trunnion-slate-800 py-2 pl-9 pr-3 text-sm text-white placeholder-trunnion-slate-400 focus:outline-none focus:ring-2 focus:ring-trunnion-gold-500"
        />
      </div>

      {/* Available tools */}
      <div className="max-h-40 overflow-y-auto rounded-lg border border-trunnion-slate-700">
        {tools.length === 0 && (
          <p className="px-3 py-4 text-center text-xs text-trunnion-slate-400">
            No tools found
          </p>
        )}
        {tools
          .filter((t) => !selectedIds.has(t.id))
          .map((tool) => (
            <button
              key={tool.id}
              onClick={() => addTool(tool.id, tool.name)}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-trunnion-slate-800 transition-colors border-b border-trunnion-slate-700/50 last:border-b-0"
            >
              <Wrench className="h-3.5 w-3.5 text-trunnion-slate-400" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white truncate">{tool.name}</p>
                <p className="text-xs text-trunnion-slate-400 truncate">
                  {tool.description}
                </p>
              </div>
            </button>
          ))}
      </div>
    </div>
  );
}
